import { Form, useDataProvider, useLogin, useTranslate } from 'react-admin';
import { useState } from 'react';
import { Grid, Card, CardHeader, CardContent, Box, Button } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import CustomLoginPasswordInput from '@/components/login/CustomLoginPasswordInput';
import CustomLoginEmailInput from '@/components/login/CustomLoginEmailInput';
import localStorageJWTManager from '@/services/localStorageJwtManager';
import MaasloopIcon from '@/assets/icons/maasloop.svg?react';
import RevloopIcon from '@/assets/icons/revloop.svg?react';
import { Palette } from '@/themes/themeOverrides/Palette';
import LanguageSwitcher from '@/components/global/menu/LanguageSwitcher.jsx';

const LoginPage = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const login = useLogin();
    const navigate = useNavigate();
    const translate = useTranslate();
    const dataProvider = useDataProvider();

    const handleSubmit = data => {
        setLoading(true);
        setError(false);
        login(data)
            .then(() => {
                const language = localStorageJWTManager.getUpdateLanguageAfterLogin();
                if (language) {
                    localStorageJWTManager.eraseUpdateLanguageAfterLogin();
                    dataProvider.put('user-account', { data: { language } });
                }
                const redirectUrl = localStorageJWTManager.getRedirectAfterLogin();
                if (redirectUrl) {
                    localStorageJWTManager.eraseRedirectAfterLogin();
                    navigate(redirectUrl);

                    return;
                }

                navigate('/');
            })
            .catch(() => {
                setError(true);
                setLoading(false);
            });
    };

    return (
        <>
            <Box sx={{ position: 'fixed', top: 32, right: 32 }}>
                <LanguageSwitcher />
            </Box>
            <Box height={'98vh'} display='flex' alignItems='center' justifyContent='center' gap={4} p={2}>
                <Card
                    sx={{
                        width: 400,
                        borderRadius: '32px',
                        paddingInline: '40px',
                        paddingBlock: '46px',
                        boxShadow: Palette.boxShadow.elevation3,
                    }}
                >
                    <Box sx={{ display: 'flex', gap: '16px', marginBottom: '26px' }}>
                        <RevloopIcon />
                        <MaasloopIcon />
                    </Box>
                    <CardHeader title={translate('auth.welcome')} sx={{ padding: 0, marginBottom: '45px' }} titleTypographyProps={{ fontSize: '18px' }} />
                    <CardContent sx={{ padding: 0, '&:last-child': { padding: 0 } }}>
                        <Form mode='onChange' reValidateMode='onChange' onSubmit={handleSubmit}>
                            <Grid container>
                                <Grid
                                    item
                                    sm={12}
                                    rowSpacing={5}
                                    sx={{ '& .MuiFormLabel-root[data-shrink="false"]': { backgroundColor: Palette.white, width: '100%' } }}
                                >
                                    <CustomLoginEmailInput isRequired={true} />
                                    <CustomLoginPasswordInput
                                        source='password'
                                        label={<span>{translate('auth.password')}</span>}
                                        isRequired={true}
                                        fullWidth
                                        error={error}
                                    />
                                </Grid>
                                <Grid item sm={12} sx={{ width: '100%' }}>
                                    <Button
                                        color='primary'
                                        size='small'
                                        type='submit'
                                        variant='contained'
                                        fullWidth
                                        sx={{ marginRight: 4, padding: '12px', marginTop: '20px' }}
                                        disabled={loading}
                                    >
                                        {translate('auth.continue')}
                                    </Button>
                                    <Box sx={{ fontSize: '14px', marginTop: '24px' }}>
                                        {translate('auth.forgotPassword')}
                                        <Link
                                            style={{ color: Palette.blue.blue600, textDecoration: 'none', fontSize: '14px', marginLeft: '4px' }}
                                            to='/forget-password'
                                        >
                                            {translate('auth.resetPassword')}
                                        </Link>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Form>
                    </CardContent>
                </Card>
            </Box>
        </>
    );
};

export default LoginPage;
